import { has, isEmpty, merge } from "lodash";
import { ButtonData_Type, HTMLData_Source, Node_Type } from "./../../../../ricos-schema/legacy/wix/rich_content/v1";
import { roundNumericValue } from "../utils";
import { convertStructToJson } from "./convertStructToJson";
import { updatePrivacyField } from "./utils";
const parseLink = ({
  url,
  rel,
  target,
  anchor,
  customData
}) => ({
  anchor,
  url,
  rel: rel && Object.entries(rel).flatMap(([key, value]) => value ? key : []).join(" "),
  target: target && "_" + target.toLowerCase(),
  customData
});
const convertVideoData = (data) => {
  const videoSrc = data.video?.src;
  const { src, width, height } = data.thumbnail || {};
  if (videoSrc?.url) {
    data.src = videoSrc.url;
    data.metadata = { thumbnail_url: src?.url, width, height, title: data.title };
  } else if (videoSrc?.id || videoSrc?.custom) {
    const pathname = src?.id || src?.custom;
    data.src = {
      pathname: videoSrc?.id || videoSrc?.custom,
      thumbnail: pathname ? { pathname, width, height } : void 0
    };
  }
  data.duration = data.video?.duration;
  delete data.video;
  delete data.title;
  delete data.thumbnail;
};
const convertAudioData = (data) => {
  const { coverImage } = data;
  if (coverImage) {
    const { width, height } = coverImage;
    const id = coverImage?.id ?? coverImage?.src?.id;
    const file_name = coverImage?.file_name ?? coverImage?.src?.id;
    data.coverImage = {
      id,
      file_name,
      width,
      height
    };
  }
};
const convertExternalData = (data) => {
  const draftData = convertStructToJson(data);
  Object.entries(draftData).forEach(([k, v]) => {
    data[k] = v;
  });
  delete data.fields;
};
const convertDividerData = (data) => {
  data.type = data.lineStyle?.toLowerCase();
  delete data.lineStyle;
  data.config = data.config || {};
  if (has(data, "width")) {
    data.config.size = data.width?.toLowerCase();
    delete data.width;
  }
  if (has(data, "alignment")) {
    data.config.alignment = data.alignment?.toLowerCase();
    delete data.alignment;
  }
  delete data.containerData;
};
var GalleryLayout = /* @__PURE__ */ ((GalleryLayout2) => {
  GalleryLayout2[GalleryLayout2["COLLAGE"] = 0] = "COLLAGE";
  GalleryLayout2[GalleryLayout2["MASONRY"] = 1] = "MASONRY";
  GalleryLayout2[GalleryLayout2["GRID"] = 2] = "GRID";
  GalleryLayout2[GalleryLayout2["THUMBNAIL"] = 3] = "THUMBNAIL";
  GalleryLayout2[GalleryLayout2["SLIDER"] = 4] = "SLIDER";
  GalleryLayout2[GalleryLayout2["SLIDESHOW"] = 5] = "SLIDESHOW";
  GalleryLayout2[GalleryLayout2["PANORAMA"] = 6] = "PANORAMA";
  GalleryLayout2[GalleryLayout2["COLUMN"] = 7] = "COLUMN";
  GalleryLayout2[GalleryLayout2["MAGIC"] = 8] = "MAGIC";
  GalleryLayout2[GalleryLayout2["FULLSIZE"] = 9] = "FULLSIZE";
  return GalleryLayout2;
})(GalleryLayout || {});
const convertGalleryStyles = (options) => {
  const styles = {};
  has(options, "layout.type") && (styles.galleryLayout = parseInt(GalleryLayout[options.layout.type]));
  has(options, "layout.horizontalScroll") && (styles.oneRow = options.layout.horizontalScroll);
  has(options, "layout.orientation") && (styles.isVertical = options.layout.orientation === "COLUMNS");
  has(options, "layout.numberOfColumns") && (styles.numberOfImagesPerRow = options.layout.numberOfColumns);
  has(options, "layout.mobileNumberOfColumns") && (styles.m_numberOfImagesPerRow = options.layout.mobileNumberOfColumns);
  has(options, "item.targetSize") && (styles.gallerySizePx = options.item.targetSize);
  has(options, "item.ratio") && (styles.cubeRatio = options.item.ratio);
  has(options, "item.crop") && (styles.cubeType = options.item.crop.toLowerCase());
  has(options, "item.spacing") && (styles.imageMargin = options.item.spacing);
  has(options, "thumbnails.placement") && (styles.galleryThumbnailsAlignment = options.thumbnails.placement.toLowerCase());
  has(options, "thumbnails.spacing") && (styles.thumbnailSpacings = Math.trunc(options.thumbnails.spacing));
  return styles;
};
const convertGalleryItem = (item) => {
  const type = has(item, "image") && !isEmpty(item.image) ? "image" : "video";
  const {
    src: { url },
    height,
    width
  } = item[type]?.media || { src: {} };
  item.url = url;
  item.metadata = { height, width, type };
  has(item, "title") && (item.metadata.title = item.title);
  has(item, "altText") && (item.metadata.altText = item.altText);
  if (has(item, "video.thumbnail")) {
    const {
      src: { url: url2 },
      height: height2,
      width: width2
    } = item.video.thumbnail;
    item.metadata.poster = { url: url2, height: height2, width: width2 };
  }
  item.image?.link && (item.metadata.link = parseLink(item.image.link));
  delete item.video;
  delete item.image;
  delete item.title;
  delete item.altText;
  return item;
};
const convertGalleryData = (data) => {
  has(data, "items") && (data.items = data.items.map((item) => convertGalleryItem(item)));
  has(data, "options") && (data.styles = convertGalleryStyles(data.options));
  delete data.options;
};
const convertImageData = (data) => {
  const { link, config, image, altText, caption } = data;
  const { src, width, height } = image || {};
  const id = src?.id || src?.custom;
  data.src = id ? { id, file_name: id, width, height } : { url: src?.url, source: "static", width, height };
  const links = {
    ...link?.anchor ? { anchor: link?.anchor } : {},
    ...link ? { link: parseLink(link) } : {}
  };
  if (links.link?.customData) {
    const parsedCustomData = parseLinkCustomData(links.link?.customData);
    merge(links.link, parsedCustomData);
    if (!parsedCustomData.customData) {
      delete links.link.customData;
    }
  }
  data.config = { ...config || {}, ...links };
  data.metadata = typeof altText === "string" || typeof caption === "string" ? {
    caption,
    alt: altText
  } : void 0;
  delete data.image;
  delete data.link;
  delete data.caption;
  delete data.altText;
};
const convertGIFData = (data) => {
  const { original = {}, downsized = {}, height, width } = data;
  data.gif = {
    originalUrl: original.gif,
    originalMp4: original.mp4,
    stillUrl: original.still,
    downsizedUrl: downsized.gif,
    downsizedSmallMp4: downsized.mp4,
    downsizedStillUrl: downsized.still,
    height,
    width
  };
  delete data.original;
  delete data.downsized;
  delete data.height;
  delete data.width;
};
const convertPollData = (data) => {
  has(data, "layout.poll.type") && (data.layout.poll.type = data.layout.poll.type.toLowerCase());
  has(data, "layout.poll.direction") && (data.layout.poll.direction = data.layout.poll.direction.toLowerCase());
  if (has(data, "layout.options")) {
    data.layout.option = data.layout.options;
    delete data.layout.options;
  }
  if (has(data, "design")) {
    const { poll = {}, options = {} } = data.design;
    const { background, borderRadius } = poll;
    const getBackground = (background2) => background2?.gradient ? {
      angle: background2?.gradient?.angle,
      start: background2?.gradient?.startColor,
      end: background2?.gradient?.lastColor
    } : background2?.color?.charAt?.(0) === "#" ? background2?.color : background2?.image?.src?.url;
    data.design = {
      poll: {
        backgroundType: background?.type?.toLowerCase(),
        background: getBackground(background),
        borderRadius: `${borderRadius}px`
      },
      option: { borderRadius: `${options.borderRadius}px` }
    };
  }
  has(data, "poll.pollId") && (data.poll.id = data.poll.pollId);
  delete data.poll.pollId;
  has(data, "poll.creatorId") && (data.poll.createdBy = data.poll.creatorId);
  delete data.poll.creatorId;
  has(data, "poll.image.src.url") && (data.poll.mediaId = data.poll.image.src.url);
  delete data.poll.image;
  has(data, "poll.options") && (data.poll.options = data.poll.options.map(({ image, ...rest }) => ({
    mediaId: image?.src?.url,
    ...rest
  })));
  if (has(data, "poll.settings")) {
    const { showVotesCount, showVoters, permissions } = data.poll.settings;
    const { view, vote, allowMultipleVotes } = permissions || {};
    const getResultsVisibility = (view2) => view2 === "EVERYONE" ? "ALWAYS" : view2 === "VOTERS" ? "VOTERS_ONLY" : "ONLY_ME";
    data.poll.settings = {
      resultsVisibility: getResultsVisibility(view),
      multipleVotes: allowMultipleVotes,
      voteRole: vote,
      votersDisplay: showVoters,
      votesDisplay: showVotesCount
    };
  }
  data.config.size = "large";
  data.config.width = "full-width";
};
const convertAppEmbedData = (data) => {
  const { type, itemId, name, image, url, bookingData, eventData, imageSrc } = data;
  const { src, width, height } = image || {};
  data.type = type.toLowerCase();
  const selectedProduct = {
    id: itemId,
    name,
    imageSrc: src?.url || imageSrc,
    pageUrl: url,
    ...bookingData || {},
    ...eventData || {}
  };
  width && (selectedProduct.imageWidth = width);
  height && (selectedProduct.imageHeight = height);
  data.selectedProduct = selectedProduct;
  delete data.id;
  delete data.itemId;
  delete data.name;
  delete data.imageSrc;
  delete data.image;
  delete data.url;
  bookingData && delete data.bookingData;
  eventData && delete data.eventData;
};
const convertLinkPreviewData = (data) => {
  if (data.thumbnailUrl) {
    data.thumbnail_url = data.thumbnailUrl;
    delete data.thumbnailUrl;
  }
  if (data.link) {
    data.config.link = parseLink(data.link);
    delete data.link;
  }
};
const convertFileData = (data) => {
  const { url, id, custom, private: isPrivate } = data.src || {};
  data.url = url;
  data.id = id || custom;
  updatePrivacyField(data, isPrivate);
  delete data.src;
  if (data.sizeInKb) {
    data.size = Number(data.sizeInKb) * 1e3;
    delete data.sizeInKb;
  }
};
const convertButtonData = (data) => {
  const { link, text, type, styles } = data;
  const { colors, border } = styles || {};
  const { width, radius } = border || {};
  const convertedLink = type === ButtonData_Type.LINK && link ? parseLink(link) : {};
  data.button = {
    settings: {
      buttonText: text,
      ...convertedLink
    },
    design: {
      borderRadius: roundNumericValue(radius),
      borderWidth: roundNumericValue(width),
      background: colors?.background,
      color: colors?.text,
      borderColor: colors?.border
    }
  };
  delete data.link;
  delete data.text;
  delete data.type;
  delete data.styles;
};
const convertHTMLData = (data) => {
  const { html, url, source } = data;
  const srcType = typeof html === "string" ? "html" : "url";
  data.srcType = srcType;
  data.src = (html || url) ?? "";
  source === HTMLData_Source.ADSENSE && (data.config.isAdsense = true);
  delete data.source;
  delete data[srcType];
};
const convertMapData = (data) => {
  const {
    draggable,
    marker,
    streetViewControl,
    zoomControl,
    locationName,
    viewModeControl,
    initialZoom,
    mapType
  } = data.mapSettings;
  data.mapSettings.isDraggingAllowed = draggable;
  data.mapSettings.isMarkerShown = marker;
  data.mapSettings.isStreetViewControlShown = streetViewControl;
  data.mapSettings.isZoomControlShown = zoomControl;
  data.mapSettings.locationDisplayName = locationName;
  data.mapSettings.zoom = initialZoom;
  data.mapSettings.mode = mapType?.toLowerCase();
  delete data.mapSettings.draggable;
  delete data.mapSettings.marker;
  delete data.mapSettings.streetViewControl;
  delete data.mapSettings.zoomControl;
  delete data.mapSettings.locationName;
  delete data.mapSettings.initialZoom;
  delete data.mapSettings.mapType;
  if (viewModeControl) {
    data.mapSettings.isViewControlShown = viewModeControl;
    delete data.mapSettings.viewModeControl;
  }
};
const convertEmbedData = (data) => {
  data.config = {
    ...data?.config || {},
    alignment: "center",
    size: "content",
    link: { url: data.src, target: "_blank", rel: "noopener" }
  };
  const { html, thumbnailUrl, title, description } = data.oembed;
  data.html = html;
  thumbnailUrl && (data.thumbnail_url = thumbnailUrl);
  typeof title === "string" && (data.title = title);
  description && (data.description = description);
  delete data.oembed;
  delete data.src;
};
const parseLinkCustomData = (customData) => {
  try {
    return { customData: JSON.parse(customData) };
  } catch (e) {
    console.error("failed to parse customData", customData);
    return { customData };
  }
};
const converters = {
  [Node_Type.VIDEO]: convertVideoData,
  [Node_Type.AUDIO]: convertAudioData,
  [Node_Type.DIVIDER]: convertDividerData,
  [Node_Type.FILE]: convertFileData,
  [Node_Type.GIF]: convertGIFData,
  [Node_Type.IMAGE]: convertImageData,
  [Node_Type.POLL]: convertPollData,
  [Node_Type.APP_EMBED]: convertAppEmbedData,
  [Node_Type.LINK_PREVIEW]: convertLinkPreviewData,
  [Node_Type.BUTTON]: convertButtonData,
  [Node_Type.HTML]: convertHTMLData,
  [Node_Type.EMBED]: convertEmbedData,
  [Node_Type.GALLERY]: convertGalleryData,
  [Node_Type.EXTERNAL]: convertExternalData
};
export {
  converters
};
//# sourceMappingURL=convertDraftPluginDataNoInnerRCE.js.map
